import { createElement, useState, useEffect } from 'react';
import {
  createRootRoute,
  Outlet,
  Navigate,
  useNavigate,
  useLocation,
} from '@tanstack/react-router';
import { useAuth } from '../hooks/useAuth.js';
import { useTheme } from '../hooks/useTheme.js';
import { useServiceState, ServiceContext } from '../hooks/useService.js';
import { Sidebar } from '../components/layout/Sidebar.js';
import { Header } from '../components/layout/Header.js';

/**
 * @graph-node {Page}
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business アプリのルートレイアウト。認証ガード・OAuth後リダイレクト復元・サービス切替時のナビ自動遷移を制御
 * @graph-connects useAuth [calls] 認証状態管理フック
 * @graph-connects useTheme [calls] テーマ切替フック
 * @graph-connects useServiceState [calls] サービス選択状態フック
 * @graph-connects Sidebar [calls] サイドバーナビゲーション描画
 * @graph-connects Header [calls] ヘッダー描画
 */
export const Route = createRootRoute({
  component: RootLayout,
});

/** @graph-connects none */
function RootLayout() {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const serviceState = useServiceState();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const isLoginPage = location.pathname === '/login';

  // OAuth後のリダイレクト復元
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const saved = sessionStorage.getItem('ux_mirror_redirect');
      if (saved) {
        sessionStorage.removeItem('ux_mirror_redirect');
        void navigate({ to: saved });
      }
    }
  }, [isLoading, isAuthenticated, navigate]);

  // data-service 属性の同期
  useEffect(() => {
    if (serviceState.service) {
      document.documentElement.setAttribute('data-service', serviceState.service);
    } else {
      document.documentElement.removeAttribute('data-service');
    }
  }, [serviceState.service]);

  // サービス切替時のナビゲーション
  useEffect(() => {
    if (isLoading || !isAuthenticated || isLoginPage) return;
    if (serviceState.service === 'styling_console' && !location.pathname.startsWith('/styling')) {
      void navigate({ to: '/styling/overview' });
    } else if (
      serviceState.service !== 'styling_console' &&
      location.pathname.startsWith('/styling')
    ) {
      void navigate({ to: '/overview' });
    }
  }, [isLoading, isAuthenticated, isLoginPage, serviceState.service, location.pathname, navigate]);

  // ログインページ
  if (isLoginPage) {
    return <Outlet />;
  }

  // ローディング中
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-base)] flex items-center justify-center">
        <div className="bg-mesh" />
        <div className="loading-spinner" />
      </div>
    );
  }

  // 未認証 → ログインへリダイレクト
  if (!isAuthenticated) {
    return <Navigate to="/login" search={{ redirect: location.pathname }} />;
  }

  return (
    <div className="min-h-screen">
      {/* 背景メッシュグラデーション */}
      <div className="bg-mesh">
        <div className="bg-mesh-extra" />
      </div>

      {/* ヘッダー */}
      <Header
        user={user}
        onLogout={() => {
          void logout();
        }}
        onMenuToggle={() => {
          setIsSidebarOpen(true);
        }}
        theme={theme}
        onThemeToggle={toggleTheme}
        service={serviceState.service}
        onServiceChange={serviceState.setService}
      />

      {createElement(
        ServiceContext.Provider,
        { value: serviceState },
        <div className="flex min-h-[calc(100vh-3.5rem)]">
          {/* サイドバー */}
          <Sidebar
            isOpen={isSidebarOpen}
            onClose={() => {
              setIsSidebarOpen(false);
            }}
          />

          {/* メインコンテンツ */}
          <main className="flex-1 relative">
            <div className="p-4 md:p-6 lg:p-8 animate-content-reveal">
              <Outlet />
            </div>
          </main>
        </div>,
      )}
    </div>
  );
}
